"use client";

import { Button } from "@package/subframe/components/Button";
import { IconWithBackground } from "@package/subframe/components/IconWithBackground";
import { IconWithLogoBackground } from "@package/subframe/components/IconWithLogoBackground";
import { LinkButton } from "@package/subframe/components/LinkButton";
import { TextField } from "@package/subframe/components/TextField";
import {
  FeatherCheck,
  FeatherEye,
  FeatherEyeOff,
  FeatherMail,
  FeatherX,
} from "@subframe/core";
import {
  createFileRoute,
  Link,
  useNavigate,
  useSearch,
} from "@tanstack/react-router";
import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";

import { Spinner } from "@/components/ui/spinner";
import { useAuth } from "@/contexts/AuthContext";
import { useRegisterUserCommandControllerRegister } from "@/generated/auth/auth";
import {
  useAcceptInvitationCommandControllerAccept,
  useGetInvitationQueryControllerGetInvitation,
} from "@/generated/invitations/invitations";
import { GetInvitationResponseDtoStatus } from "@/generated/model";
import { signIn } from "@/lib/auth";

export const Route = createFileRoute("/accept-invitation")({
  validateSearch: (search: Record<string, unknown>): { token?: string } => {
    return {
      token: search.token ? String(search.token) : undefined,
    };
  },
  component: AcceptInvitationPage,
});

function AcceptInvitationPage() {
  const { token } = useSearch({ from: "/accept-invitation" });
  const navigate = useNavigate();
  const { isLoading: authLoading, isAuthenticated } = useAuth();
  const [pageState, setPageState] = useState<
    | "loading"
    | "register"
    | "accepting"
    | "success"
    | "error"
    | "login-required"
  >("loading");
  const [errorMessage, setErrorMessage] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [password, setPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const hasAccepted = useRef(false);

  // Fetch invitation details (public endpoint)
  const {
    data: invitationData,
    isLoading: invitationLoading,
    error: invitationError,
  } = useGetInvitationQueryControllerGetInvitation(token || "", {
    query: { enabled: !!token },
  });

  const invitation = invitationData?.data;

  // Accept invitation mutation
  const { mutate: acceptInvitation } =
    useAcceptInvitationCommandControllerAccept({
      mutation: {
        onSuccess: async () => {
          window.location.href = "/dashboard";
        },
        onError: (error: unknown) => {
          setPageState("error");
          setErrorMessage(
            error instanceof Error
              ? error.message
              : "Failed to accept invitation",
          );
        },
      },
    });

  // Register user mutation
  const { mutate: registerUser, isPending: isRegistering } =
    useRegisterUserCommandControllerRegister({
      mutation: {
        onError: (error: unknown) => {
          // If registration fails, the user likely already exists
          const msg = error instanceof Error ? error.message : "";
          if (
            msg.includes("already exists") ||
            msg.includes("already registered")
          ) {
            setPageState("login-required");
          } else {
            toast.error(msg || "Registration failed. Please try again.");
          }
        },
      },
    });

  const passwordRequirements = [
    { text: "At least 8 characters", met: password.length >= 8 },
    { text: "One uppercase letter", met: /[A-Z]/.test(password) },
    { text: "One number", met: /\d/.test(password) },
  ];

  const passwordsMatch = password === confirmPassword && password.length > 0;
  const allRequirementsMet = passwordRequirements.every((req) => req.met);
  const canSubmit =
    firstName.trim() &&
    lastName.trim() &&
    allRequirementsMet &&
    passwordsMatch &&
    !isRegistering;

  // Determine page state based on auth + invitation status
  useEffect(() => {
    // Don't override terminal states (already accepted or in progress)
    if (hasAccepted.current) return;

    if (!token || invitationLoading || authLoading) return;

    if (invitationError) {
      setPageState("error");
      setErrorMessage("This invitation link is invalid or has expired.");
      return;
    }

    if (!invitation) return;

    // Check invitation is still valid
    if (invitation.status !== GetInvitationResponseDtoStatus.PENDING) {
      setPageState("error");
      setErrorMessage(
        invitation.status === GetInvitationResponseDtoStatus.ACCEPTED
          ? "This invitation has already been accepted."
          : invitation.status === GetInvitationResponseDtoStatus.REVOKED
            ? "This invitation has been revoked."
            : "This invitation is no longer valid.",
      );
      return;
    }

    if (new Date(invitation.expiresAt) < new Date()) {
      setPageState("error");
      setErrorMessage(
        "This invitation has expired. Please ask the nursery group admin to send a new one.",
      );
      return;
    }

    if (isAuthenticated) {
      // Authenticated user — auto-accept
      hasAccepted.current = true;
      setPageState("accepting");
      acceptInvitation({ data: { token } });
    } else {
      // Not authenticated — show register form
      setPageState("register");
    }
  }, [
    token,
    invitationLoading,
    invitationError,
    invitation,
    authLoading,
    isAuthenticated,
    acceptInvitation,
  ]);

  // Handle registration form submit
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!invitation || !token || !canSubmit) return;

    registerUser(
      {
        data: {
          email: invitation.email,
          name: `${firstName.trim()} ${lastName.trim()}`,
          password,
        },
      },
      {
        onSuccess: async () => {
          try {
            await signIn(invitation.email, password);
          } catch {
            toast.error(
              "Account created but sign-in failed. Please log in manually.",
            );
            setPageState("login-required");
          }
        },
      },
    );
  };

  const friendlyRole =
    invitation?.roleOffered === "ADMIN" ? "Group Administrator" : "Member";

  // No token provided
  if (!token) {
    return (
      <PageWrapper>
        <IconWithLogoBackground
          variant="error"
          size="x-large"
          icon={<FeatherX />}
        />
        <div className="flex flex-col items-center gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font text-center">
            Invalid Invitation Link
          </span>
          <span className="text-body font-body text-subtext-color text-center">
            This invitation link is missing a token. Please check the link in
            your email and try again.
          </span>
        </div>
        <Button
          variant="brand-primary"
          size="large"
          onClick={() => navigate({ to: "/login" })}
        >
          Go to Login
        </Button>
      </PageWrapper>
    );
  }

  // Loading state
  if (pageState === "loading") {
    return (
      <PageWrapper>
        <Spinner className="h-8 w-8 text-primary" />
      </PageWrapper>
    );
  }

  // Registration form for new users
  if (pageState === "register" && invitation) {
    return (
      <div className="flex w-full items-center justify-center min-h-screen bg-default-background">
        <div className="flex w-full max-w-[480px] flex-col items-center gap-8 p-8">
          {/* Logo */}
          <div className="flex items-center gap-2">
            <img
              className="h-8 flex-none object-cover"
              src="/images/logo.svg"
              alt="Logo"
            />
          </div>

          {/* Invitation info */}
          <div className="flex flex-col items-center gap-2">
            <IconWithLogoBackground
              variant="brand"
              size="x-large"
              icon={<FeatherMail />}
            />
            <span className="text-heading-2 font-heading-2 text-default-font text-center">
              You&apos;ve been invited
            </span>
            <span className="text-body font-body text-subtext-color text-center">
              You&apos;ve been invited to join{" "}
              <strong>{invitation.organisationName}</strong> as a{" "}
              <strong>{friendlyRole}</strong>. Create your account to get
              started.
            </span>
          </div>

          {/* Registration form */}
          <form
            onSubmit={handleSubmit}
            className="flex w-full flex-col items-start gap-5"
          >
            {/* Email (read-only) */}
            <TextField className="h-auto w-full" size="large" label="Email">
              <TextField.Input type="email" value={invitation.email} disabled />
            </TextField>

            {/* Name fields */}
            <div className="flex w-full gap-4">
              <TextField
                className="h-auto w-full"
                size="large"
                label="First name"
              >
                <TextField.Input
                  placeholder="First name"
                  value={firstName}
                  onChange={(e) => setFirstName(e.target.value)}
                  disabled={isRegistering}
                />
              </TextField>

              <TextField
                className="h-auto w-full"
                size="large"
                label="Last name"
              >
                <TextField.Input
                  placeholder="Last name"
                  value={lastName}
                  onChange={(e) => setLastName(e.target.value)}
                  disabled={isRegistering}
                />
              </TextField>
            </div>

            {/* Password */}
            <TextField
              className="h-auto w-full"
              size="large"
              label="Password"
              iconRight={
                <button
                  type="button"
                  onClick={() => setShowPassword(!showPassword)}
                  className="text-subtext-color hover:text-default-font"
                >
                  {showPassword ? <FeatherEyeOff /> : <FeatherEye />}
                </button>
              }
            >
              <TextField.Input
                type={showPassword ? "text" : "password"}
                placeholder="Create a password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                autoComplete="new-password"
                disabled={isRegistering}
              />
            </TextField>

            {/* Confirm Password */}
            <TextField
              className="h-auto w-full"
              size="large"
              label="Confirm password"
              error={confirmPassword.length > 0 && !passwordsMatch}
              helpText={
                confirmPassword.length > 0 && !passwordsMatch
                  ? "Passwords do not match"
                  : ""
              }
              iconRight={
                <button
                  type="button"
                  onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                  className="text-subtext-color hover:text-default-font"
                >
                  {showConfirmPassword ? <FeatherEyeOff /> : <FeatherEye />}
                </button>
              }
            >
              <TextField.Input
                type={showConfirmPassword ? "text" : "password"}
                placeholder="Confirm your password"
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                autoComplete="new-password"
                disabled={isRegistering}
              />
            </TextField>

            {/* Password Requirements */}
            <div className="flex w-full flex-col items-start gap-3">
              <span className="text-body-bold font-body-bold text-default-font">
                Password must contain:
              </span>
              <div className="flex w-full flex-col items-start gap-2">
                {passwordRequirements.map((requirement, index) => (
                  <div key={index} className="flex items-center gap-2">
                    <IconWithBackground
                      variant={requirement.met ? "success" : "neutral"}
                      size="x-small"
                      icon={<FeatherCheck />}
                      square={false}
                    />
                    <span
                      className={`text-body font-body ${requirement.met ? "text-success-700" : "text-subtext-color"}`}
                    >
                      {requirement.text}
                    </span>
                  </div>
                ))}
              </div>
            </div>

            <Button
              className="h-12 w-full flex-none"
              variant="brand-primary"
              size="large"
              type="submit"
              disabled={!canSubmit}
              loading={isRegistering}
            >
              Create account & join
            </Button>
          </form>

          <div className="flex w-full items-center justify-center">
            <div className="flex flex-wrap items-start gap-1">
              <span className="text-caption font-caption text-neutral-900">
                Already have an account?
              </span>
              <Link
                to="/login"
                search={{ redirect: `/accept-invitation?token=${token}` }}
              >
                <LinkButton variant="brand" size="small">
                  Sign in
                </LinkButton>
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  // Existing user needs to log in
  if (pageState === "login-required") {
    return (
      <PageWrapper>
        <IconWithLogoBackground
          variant="brand"
          size="x-large"
          icon={<FeatherMail />}
        />
        <div className="flex flex-col items-center gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font text-center">
            Account Already Exists
          </span>
          <span className="text-body font-body text-subtext-color text-center">
            An account with this email already exists. Please sign in to accept
            the invitation.
          </span>
        </div>
        <Link
          to="/login"
          search={{ redirect: `/accept-invitation?token=${token}` }}
        >
          <Button variant="brand-primary" size="large">
            Sign in
          </Button>
        </Link>
      </PageWrapper>
    );
  }

  // Accepting state
  if (pageState === "accepting") {
    return (
      <PageWrapper>
        <IconWithLogoBackground
          variant="brand"
          size="x-large"
          icon={<FeatherMail />}
        />
        <div className="flex flex-col items-center gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font text-center">
            Accepting Invitation
          </span>
          <span className="text-body font-body text-subtext-color text-center">
            Please wait while we process your invitation...
          </span>
        </div>
        <Spinner className="h-6 w-6 text-primary" />
      </PageWrapper>
    );
  }

  // Success state
  if (pageState === "success") {
    return (
      <PageWrapper>
        <IconWithLogoBackground
          variant="success"
          size="x-large"
          icon={<FeatherCheck />}
        />
        <div className="flex flex-col items-center gap-2">
          <span className="text-heading-2 font-heading-2 text-default-font text-center">
            Invitation Accepted
          </span>
          <span className="text-body font-body text-subtext-color text-center">
            You have successfully joined
            {invitation
              ? ` ${invitation.organisationName}`
              : " the nursery group"}
            .
          </span>
        </div>
        <Button
          variant="brand-primary"
          size="large"
          onClick={() => navigate({ to: "/dashboard" })}
        >
          Go to Dashboard
        </Button>
      </PageWrapper>
    );
  }

  // Error state
  return (
    <PageWrapper>
      <IconWithLogoBackground
        variant="error"
        size="x-large"
        icon={<FeatherX />}
      />
      <div className="flex flex-col items-center gap-2">
        <span className="text-heading-2 font-heading-2 text-default-font text-center">
          Unable to Accept Invitation
        </span>
        <span className="text-body font-body text-subtext-color text-center">
          {errorMessage}
        </span>
      </div>
      <Button
        variant="brand-primary"
        size="large"
        onClick={() => navigate({ to: "/login" })}
      >
        Go to Login
      </Button>
    </PageWrapper>
  );
}

function PageWrapper({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex min-h-screen items-center justify-center bg-default-background">
      <div className="flex max-w-md flex-col items-center gap-6 p-8">
        {children}
      </div>
    </div>
  );
}
